import React, { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { ContractorPortalCertificate } from 'src/interfaces/contractorPortal'
import tw from 'twin.macro'
import { ImportMinor, ViewMinor } from '@shopify/polaris-icons'
import { MenuActionItem } from 'src/interfaces/ui'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import { downloadBlob, openBlobInTab } from 'src/utils/fileDownload'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ContractorPortalDownloadCertificate } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { ActionButton } from '../../shared/ActionButton'
import { ActionMenu } from '../../shared/ActionMenu'
import { replaceTrainingWording } from 'src/utils/certificationWording'

interface Props {
  certificate: ContractorPortalCertificate
  isFirstItem?: boolean
}

interface CertificateFileActionOptions {
  onFile(blob: Blob): void
  onError?(): void
  errorMessage: string
}

const ContractorPortalCertificateItemComponent = ({ certificate, isFirstItem }: Props) => {
  const dispatch = useDispatch()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const completedAtLabel = useMemo(() => {
    return dayjs.unix(certificate.completedAt).format('MMMM DD, YYYY')
  }, [certificate.completedAt])

  const handleAction = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
    const anchor = event.currentTarget

    setAnchorEl(anchor)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const runCertificateFileAction = useCallback(
    async ({ onFile, onError, errorMessage }: CertificateFileActionOptions) => {
      handleCloseMenu()
      setIsLoading(true)

      try {
        const blob: Blob = await createAsyncAction(
          dispatch,
          ContractorPortalDownloadCertificate.request({
            certificateId: certificate.id,
          }),
        )

        onFile(blob)
      } catch (err) {
        onError?.()

        showToast(errorMessage, {
          variant: 'error',
        })
      }

      setIsLoading(false)
    },
    [certificate.id, dispatch, handleCloseMenu],
  )

  const handleView = useCallback(async () => {
    const tab = window.open('', '_blank')

    await runCertificateFileAction({
      onFile: (blob) => {
        if (tab) {
          openBlobInTab(tab, blob)
        } else {
          downloadBlob(blob, certificate.fileName)
        }
      },
      onError: () => {
        tab?.close()
      },
      errorMessage: 'Failed to open certificate',
    })
  }, [certificate.fileName, runCertificateFileAction])

  const handleDownload = useCallback(async () => {
    await runCertificateFileAction({
      onFile: (blob) => {
        downloadBlob(blob, certificate.fileName)
      },
      errorMessage: 'Failed to download certificate',
    })
  }, [certificate.fileName, runCertificateFileAction])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    return [
      {
        label: 'View certificate',
        icon: <ViewMinor width={20} height={20} />,
        onClick: handleView,
      },
      {
        label: 'Download certificate',
        icon: <ImportMinor width={20} height={20} />,
        onClick: handleDownload,
      },
    ]
  }, [handleView, handleDownload])

  return (
    <div
      css={[
        tw`p-4 bg-shades-0 border-0 border-t border-solid border-shades-100`,
        isFirstItem && tw`border-none`,
      ]}
      data-testid={`contractors-list-certificates-row-${certificate.id}`}
    >
      <div css={tw`flex items-center flex-row justify-between`}>
        <div css={tw`max-w-[60%]`}>
          <Typography variant="h4" css={tw`mb-0.5 [word-wrap: break-word]`}>
            {replaceTrainingWording(certificate.trainingTitle)}
          </Typography>
          <Typography css={tw`mb-0.5 text-shades-400`}>{completedAtLabel}</Typography>
        </div>
        <ActionButton
          selected={!!anchorEl}
          color="grey"
          text="Actions"
          loading={isLoading}
          onClick={handleAction}
          dataTestId={`contractors-list-certificate-${certificate.id}-menu-btn`}
        />
        <ActionMenu
          anchorEl={anchorEl}
          items={actionItems}
          isOpen={isMenuOpen}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={handleCloseMenu}
        />
      </div>
    </div>
  )
}

export const ContractorPortalCertificateItem = memo(ContractorPortalCertificateItemComponent)
